import { useEffect, useMemo, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { ChatMessage, MessageMetadata } from "@racoon/api";
import {
  fmt,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Select,
  Button,
  DataTable,
  type Column,
} from "@racoon/ui";
import { useSaveDashboardMutation } from "~/shared/api/chat/chat.api";
import { exportToExcel } from "~/shared/lib/export-utils";
import { useT, Namespace } from "@racoon/i18n";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
);

// Helper: detect if a value is a date string
function isDate(val: any) {
  if (typeof val !== "string") return false;
  // Simple ISO or YYYY-MM-DD check
  return /^\d{4}-\d{2}-\d{2}/.test(val) || !isNaN(Date.parse(val));
}

// Helper: generate a color palette for pie slices
function getColorPalette(n: number) {
  const palette = [
    "#4285F4",
    "#EA4335",
    "#FBBC05",
    "#34A853",
    "#A142F4",
    "#F44292",
    "#00B8D9",
    "#FF5630",
    "#36B37E",
    "#FFAB00",
    "#6554C0",
    "#FF8B00",
  ];
  // Repeat if not enough colors
  return Array.from({ length: n }, (_, i) => palette[i % palette.length]);
}

// Helper: smartly format MessageMetadata for Chart.js
function smartMetadataToChartData(metadata?: MessageMetadata) {
  if (!metadata || !metadata.columns || !metadata.rows) return null;

  const isNumeric = (val: any) => !isNaN(parseFloat(val)) && isFinite(val);

  // Single value case: 1 column, 1 row
  if (metadata.columns.length === 1 && metadata.rows.length === 1) {
    const col = metadata.columns[0];
    const val = metadata.rows[0][col];
    return {
      labels: [col],
      datasets: [
        {
          label: col,
          data: [isNumeric(val) ? Number(val) : 0],
          backgroundColor: getColorPalette(1)[0],
        },
      ],
    };
  }

  // Guess label column: first non-numeric column
  const labelCol =
    metadata.columns.find((col) => !isNumeric(metadata.rows[0]?.[col])) || metadata.columns[0];

  // Guess value columns: all numeric columns except labelCol
  const valueCols = metadata.columns.filter(
    (col) => col !== labelCol && isNumeric(metadata.rows[0]?.[col]),
  );

  // Fallback: if no valueCols, use all except labelCol
  const datasetsCols = valueCols.length
    ? valueCols
    : metadata.columns.filter((col) => col !== labelCol);

  // Format labels: if date, format; else, show as string
  const labels = metadata.rows.map((row) => {
    const val = row[labelCol];
    if (isDate(val)) return fmt.date(val);
    if (typeof val === "string" && val.length > 40) return val.slice(0, 37) + "...";
    return val;
  });

  const datasets = datasetsCols.map((col, i) => ({
    label: col,
    data: metadata.rows.map((row) => {
      const val = row[col];
      return isNumeric(val) ? Number(val) : null;
    }),
    backgroundColor: getColorPalette(datasetsCols.length)[i],
  }));

  return { labels, datasets };
}

export const ChatChart = ({
  message,
  onChangeChartIndex,
  messageIndex,
}: {
  message?: ChatMessage | null;
  onChangeChartIndex: (type: "prev" | "next") => void;
  messageIndex: number;
}) => {
  // For future: support multiple charts
  const charts = [message];
  const [currentChartIndex] = useState(0);
  const currentMessage = charts[currentChartIndex];
  const chartData = smartMetadataToChartData(currentMessage?.metadata);
  const [chartType, setChartType] = useState("bar");
  const [saveDashboard] = useSaveDashboardMutation();
  const [showTable, setShowTable] = useState(false);

  const t = useT();

  const chartTypes = [
    { label: t(Namespace.Charts, "barChart"), value: "bar" },
    { label: t(Namespace.Charts, "lineChart"), value: "line" },
    { label: t(Namespace.Charts, "pieChart"), value: "pie" },
    { label: "Table", value: "table" },
  ];

  // Editable title/description
  const [dashboardTitle, setDashboardTitle] = useState(
    currentMessage?.metadata?.plot_title || "Dashboard",
  );

  useEffect(() => {
    setDashboardTitle(currentMessage?.metadata?.plot_title || "Dashboard");
  }, [message]);
  const [dashboardDescription, setDashboardDescription] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [, setShowTitleInput] = useState(false);

  // Save handler
  const handleSaveDashboard = async () => {
    if (!currentMessage?.metadata?.sql_query) return;
    if (!dashboardTitle) {
      setShowTitleInput(true);
      return;
    }
    await saveDashboard({
      title: dashboardTitle,
      sql_query: currentMessage.metadata.sql_query,
      chat_id: currentMessage.id !== undefined ? String(currentMessage.id) : undefined,
      plot_config: {
        chartType,
        columns: currentMessage.metadata.columns,
        // add more config if needed
      },
      description: dashboardDescription,
    });
    setShowTitleInput(false);
    // Optionally show a toast/notification
  };

  // Legend rendering
  const renderLegend = () => {
    if (!chartData || !chartData.datasets) return null;
    return (
      <div className="flex gap-4 justify-center mb-2">
        {chartData.datasets.map((ds) => (
          <div key={ds.label} className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: ds.backgroundColor }}
            />
            <span className="text-xs font-medium" style={{ color: ds.backgroundColor }}>
              {ds.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Table rendering
  const renderTable = () => {
    if (!message?.metadata) return null;
    const { columns, rows } = message.metadata;
    if (!columns.length || !rows.length) return <div>{t(Namespace.Common, "noData")}</div>;
    // If you have a Table component in @racoon/ui, use it here:
    // return <Table columns={columns} data={rows} />;
    // Otherwise, fallback to your current table:
    return (
      <div className="overflow-auto max-h-96">
        <table className="min-w-full border">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => {
                  let val = row[col];
                  if (isDate(val)) val = fmt.date(val);
                  if (typeof val === "string" && val.length > 60)
                    return (
                      <td key={col} title={val}>
                        {val.slice(0, 57) + "..."}
                      </td>
                    );
                  return <td key={col}>{val}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Chart rendering
  const renderChart = () => {
    if (!chartData) return <div>{t(Namespace.Charts, "noDataAvailable")}</div>;
    switch (chartType) {
      case "bar":
        return <Bar data={chartData} />;
      case "line":
        return <Line data={chartData} />;
      case "pie": {
        // For pie, use only the first dataset, but assign each slice a color
        const pieColors = getColorPalette(chartData.labels.length);
        return (
          <Pie
            data={{
              labels: chartData.labels,
              datasets: [
                {
                  ...chartData.datasets[0],
                  backgroundColor: pieColors,
                  data: chartData?.datasets[0]?.data,
                },
              ],
            }}
          />
        );
      }
      case "table":
        return renderTable();
      default:
        return null;
    }
  };

  // DataTable columns and data
  const tableColumns: Column<any>[] = useMemo(() => {
    if (!currentMessage?.metadata?.columns) return [];
    return currentMessage.metadata.columns.map((col) => ({
      id: col,
      header: col,
      key: col,
      sortable: true,
    }));
  }, [currentMessage]);
  const tableData = currentMessage?.metadata?.rows || [];

  // Download as Excel
  const handleDownloadExcel = () => {
    if (!tableColumns.length || !tableData.length) return;
    exportToExcel({
      data: tableData,
      columns: tableColumns,
      filename: `${dashboardTitle || "dashboard"}.xlsx`,
      sheetName: dashboardTitle || "Dashboard",
    });
  };

  // Table rendering with DataTable
  const renderDataTable = () => (
    <div className="w-full h-[400px]">
      <DataTable columns={tableColumns} data={tableData} />
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full max-w-[600px] bg-white dark:bg-black rounded-xl p-4 pb-0 relative">
      {/* Navigation & Title Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Button
            id="chat-chart-prev-button"
            variant="outline"
            size="icon"
            onClick={() => onChangeChartIndex("prev")}
          >
            <span>&#8592;</span>
          </Button>
          <Button
            id="chat-chart-next-button"
            variant="outline"
            size="icon"
            onClick={() => onChangeChartIndex("next")}
          >
            <span>&#8594;</span>
          </Button>
          <span className="ml-2 text-base font-semibold text-gray-700 dark:text-gray-200">
            Graph {messageIndex + 1}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger id="chat-chart-type-select">
              <SelectValue placeholder={t(Namespace.Charts, "selectType")} />
            </SelectTrigger>
            <SelectContent>
              {chartTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Title & Description */}
      <div className="mb-2 text-center">
        {editingTitle ? (
          <input
            id="chat-chart-title-input"
            className="text-2xl font-bold text-center bg-transparent border-b border-gray-300 dark:border-gray-700 outline-none mb-1"
            value={dashboardTitle}
            onChange={(e) => setDashboardTitle(e.target.value)}
            onBlur={() => setEditingTitle(false)}
            autoFocus
          />
        ) : (
          <div
            className="text-2xl font-bold cursor-pointer text-gray-800 dark:text-gray-100"
            onClick={() => setEditingTitle(true)}
          >
            {dashboardTitle || t(Namespace.Common, "noTitle")}
          </div>
        )}
        {editingDesc ? (
          <textarea
            id="chat-chart-description-textarea"
            className="text-sm text-center bg-transparent border-b border-gray-200 dark:border-gray-700 outline-none mt-1"
            value={dashboardDescription}
            onChange={(e) => setDashboardDescription(e.target.value)}
            onBlur={() => setEditingDesc(false)}
            rows={2}
            autoFocus
          />
        ) : (
          <div
            className="text-sm text-gray-500 dark:text-gray-400 cursor-pointer mt-1"
            onClick={() => setEditingDesc(true)}
          >
            {dashboardDescription || "Click to add description"}
          </div>
        )}
      </div>
      {/* Legend */}
      {renderLegend()}
      {/* Chart/Table Area */}
      <div className="flex-1 flex items-center justify-center min-h-[300px]">
        {showTable ? renderDataTable() : renderChart()}
      </div>
      {/* Bottom Controls */}
      <div className="flex items-center justify-between mt-4 text-xs text-gray-400">
        <Button
          id="chat-chart-download-button"
          variant="ghost"
          size="sm"
          className="opacity-80"
          onClick={handleDownloadExcel}
          disabled={!tableColumns.length || !tableData.length}
        >
          Download as
        </Button>
        <Button
          id="chat-chart-toggle-view-button"
          variant="ghost"
          size="sm"
          className="opacity-80"
          onClick={() => setShowTable((v) => !v)}
          disabled={!tableColumns.length || !tableData.length}
        >
          {showTable ? t(Namespace.Charts, "showAsChart") : t(Namespace.Charts, "showAsTable")}
        </Button>
        <Button
          id="chat-chart-save-dashboard-button"
          variant="ghost"
          size="sm"
          className="opacity-60"
          onClick={handleSaveDashboard}
        >
          Add to favorites
        </Button>
      </div>
    </div>
  );
};
